import type { ApiResponse } from "@glow-price/shared";

import { clearSession, getToken } from "../auth/store";

const BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
const PROXIED = !!import.meta.env.VITE_API_PROXY_TARGET;

export class ApiClientError extends Error {
  code: string;
  status: number;
  details: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type RequestOpts = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
};

function buildUrl(path: string, query?: RequestOpts["query"]): string {
  const base = PROXIED ? "" : BASE;
  const url = new URL(`${base}${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v == null || v === "") continue;
      url.searchParams.set(k, String(v));
    }
  }
  return PROXIED ? `${url.pathname}${url.search}` : url.toString();
}

export async function apiRequest<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const url = buildUrl(path, opts.query);
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }

  const resp = await fetch(url, {
    method: opts.method ?? "GET",
    headers,
    body,
    signal: opts.signal,
  });
  if (resp.status === 401) clearSession();

  let json: ApiResponse<T>;
  try {
    json = (await resp.json()) as ApiResponse<T>;
  } catch {
    throw new ApiClientError(resp.status, "INVALID_RESPONSE", `HTTP ${resp.status}`);
  }
  if (!json.success) {
    throw new ApiClientError(resp.status, json.error.code, json.error.message, json.error.details);
  }
  return json.data;
}
